import React from "react";
import {
    SelectItem,
    Input,
    Switch,
} from "@heroui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@heroui/react";
import { ContriesDropdown } from "../../../components/common/countriesDropdown";
import AppSelect from "../../../components/common/appSelect";

interface LocationTravelAvailabilityProps {
    t: any;
    selectedRosterStatus: string;
    setSelectedRosterStatus: (value: string) => void;
    setRosterStatus: (value: string) => void;
    rosterStatusOptions: string[];
    errors: any;
    setErrors: (value: any) => void;
    loadRosterStatusIfNeeded: () => Promise<void>;
    formatEnumString: (status: string) => string;
    selectedLocation: string;
    setSelectedLocation: (value: string) => void;
    setLocation: (value: string) => void;
    noticePeriodDays: number;
    setNoticePeriodDays: (value: number) => void;
    travelReady: boolean;
    setTravelReady: (value: boolean) => void;
}

const LocationTravelAvailability: React.FC<LocationTravelAvailabilityProps> = ({
    t,
    selectedRosterStatus,
    setSelectedRosterStatus,
    setRosterStatus,
    rosterStatusOptions,
    errors,
    setErrors,
    loadRosterStatusIfNeeded,
    formatEnumString,
    selectedLocation,
    setSelectedLocation,
    setLocation,
    noticePeriodDays,
    setNoticePeriodDays,
    travelReady,
    setTravelReady,
}) => {
    return (
        <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-800 border-b pb-1">
                {t("location_travel_availability")}
            </h3>
            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600 block">
                    {t("roster_status")}
                </label>

                <AppSelect
                    placeholder={t("select_roster_status_placeholder")}
                    selectedKeys={
                        selectedRosterStatus ? [selectedRosterStatus] : []
                    }
                    onSelectionChange={(key) => {
                        const selected = Array.from(key)[0] as string;
                        setSelectedRosterStatus(selected);
                        setRosterStatus(selected);
                        setErrors((prev: any) => ({
                            ...prev,
                            rosterStatus: selected
                                ? ""
                                : t("roster_status_required"),
                        }));
                    }}
                    onOpenChange={(isOpen) => isOpen && loadRosterStatusIfNeeded()}
                    size="sm"
                    className="w-full"
                    classNames={{
                        trigger: "min-h-[32px] h-8",
                    }}
                    popoverProps={{
                        placement: "bottom",
                        shouldFlip: false,
                    }}
                >
                    {rosterStatusOptions.length > 0 ? (
                        rosterStatusOptions.map((status) => (
                            <SelectItem key={status}>
                                {formatEnumString(status)}
                            </SelectItem>
                        ))
                    ) : (
                        <SelectItem key="no-status" isDisabled>
                            {t("no_roster_statuses_available")}
                        </SelectItem>
                    )}
                </AppSelect>

                {errors.rosterStatus && (
                    <div className="text-red-500 text-xs">
                        {errors.rosterStatus}
                    </div>
                )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        <div className="flex items-center gap-1">
                            {t("location")}
                            <Tooltip
                                className="w-[350px]"
                                placement="right"
                                content={
                                    <div className="text-sm">
                                        <span className="font-semibold block mb-1">
                                            {t("location_tooltip_title")}
                                        </span>
                                        <span>
                                            {t("location_tooltip_description")}
                                        </span>
                                    </div>
                                }
                            >
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                            </Tooltip>
                        </div>
                    </label>
                    <ContriesDropdown
                        value={selectedLocation}
                        onLocationChange={(location) => {
                            setSelectedLocation(location);
                            setLocation(location);
                            setErrors((prev: any) => ({
                                ...prev,
                                location: location ? "" : t("location_required"),
                            }));
                        }}
                    />
                    {errors.location && (
                        <div className="text-red-500 text-xs">
                            {errors.location}
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        {t("notice_period_days")}
                    </label>
                    <Input
                        type="number"
                        placeholder={t("enter_notice_period_placeholder")}
                        value={noticePeriodDays.toString()}
                        onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            setNoticePeriodDays(value);
                            setErrors((prev: any) => ({
                                ...prev,
                                noticePeriodDays:
                                    value >= 0 ? "" : t("notice_period_required"),
                            }));
                        }}
                        size="sm"
                        classNames={{
                            input: "text-sm",
                            inputWrapper: "min-h-[32px] h-8",
                        }}
                    />
                    {errors.noticePeriodDays && (
                        <div className="text-red-500 text-xs">
                            {errors.noticePeriodDays}
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        {t("travel_ready")}
                    </label>
                    <div className="flex items-center h-8">
                        <Switch
                            isSelected={travelReady}
                            onValueChange={setTravelReady}
                            size="sm"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationTravelAvailability;
